// Central dictionary of every metric shown across InPhase. Used by <MetricInfo />
// tooltips/popovers and the /app/glossary page. Keep entries short-but-complete:
// definition, why it matters, and concrete steps to move the number.

export const METRICS = {
  visibility_index: {
    label: "Visibility Index",
    category: "Rankings & Visibility",
    quickTip: "How visible you are across all tracked keywords, as a %.",
    definition: "A single % score that blends your ranking position and search volume across every keyword you track. Ranking #1 for a high-volume term contributes far more than ranking #50 for a low-volume one.",
    whyItMatters: "It's the fastest way to see if your overall SEO is trending up or down without scanning every keyword individually. A rising Visibility Index means you're capturing more of the total available search demand.",
    howToImprove: [
      "Push keywords sitting on page 2 (positions 11-20) into page 1 — they carry the most upside per point of effort.",
      "Prioritize fixing pages behind your highest-volume keywords first; they move the score more than long-tail terms.",
      "Add more relevant keywords in the Keywords tab so the index reflects your full footprint, not just a partial sample.",
    ],
  },
  estimated_traffic: {
    label: "Estimated Organic Traffic",
    category: "Traffic",
    quickTip: "Projected monthly visits from your current keyword rankings.",
    definition: "A modeled estimate of monthly organic visits, calculated from each tracked keyword's search volume and expected click-through rate at its current ranking position.",
    whyItMatters: "Rankings alone don't pay the bills — traffic does. This number translates abstract positions into a business-relevant metric you can tie to leads and revenue.",
    howToImprove: [
      "Improve rankings on keywords already in positions 4-10 — CTR jumps sharply once you break into the top 3.",
      "Target featured snippets and 'People Also Ask' boxes, which can capture clicks even from lower positions.",
      "Add keywords with strong commercial intent and decent volume rather than only chasing vanity terms.",
    ],
  },
  tracked_keywords: {
    label: "Tracked Keywords",
    category: "Rankings & Visibility",
    quickTip: "Number of keywords being monitored for this project.",
    definition: "The total count of search terms you're actively tracking rank position, volume, and SERP features for.",
    whyItMatters: "The bigger and more relevant your keyword set, the more accurate your Visibility Index and traffic estimates are — and the earlier you catch competitors encroaching on new terms.",
    howToImprove: [
      "Add keyword variations (questions, comparisons, local modifiers) around topics you already rank for.",
      "Use the Content Brief tool to surface related terms you're not tracking yet.",
      "Remove keywords that no longer match your business to keep the data set focused.",
    ],
  },
  ai_overview_coverage: {
    label: "AI Overview Coverage",
    category: "Content & AEO",
    quickTip: "How many of your tracked keywords trigger a Google AI Overview.",
    definition: "Counts how many of your tracked keywords currently show an AI Overview (Google's AI-generated answer box) in the SERP — whether or not you're the one being cited.",
    whyItMatters: "AI Overviews are eating traditional click-through rates. Knowing which of your keywords are affected tells you where to invest in Answer Engine Optimization (AEO) to get cited instead of skipped.",
    howToImprove: [
      "Run affected pages through the Content / AEO Optimizer to structure them as direct, quotable answers.",
      "Add clear definitions, numbered steps, and FAQ schema — these formats get cited most often.",
      "Keep the direct answer concise near the top of the page; AI Overviews favor the first clear answer they find.",
    ],
  },
  ai_overview_citation: {
    label: "AI Overview Citation",
    category: "Content & AEO",
    quickTip: "Is your page one of the real sources Google's AI Overview actually cites?",
    definition: "Checks DataForSEO's real, expanded AI Overview data for this keyword to see if your domain appears in the actual list of sources Google's AI Overview cites — not just that an overview exists, but that YOU are one of the pages it quotes.",
    whyItMatters: "Showing up in an AI Overview's reference list is the modern equivalent of ranking #1 — Google is directly quoting your content as an authority. It's the single clearest real signal that your page is winning at Answer Engine Optimization (AEO).",
    howToImprove: [
      "Structure the cited section as a direct, self-contained answer (clear definition, numbered steps, or a short list) — that's the format AI Overviews quote most.",
      "Add FAQ schema and keep the most quotable sentence near the top of the page, not buried after a long intro.",
      "If you're not cited yet, study who is for this keyword — their exact phrasing and structure is a strong template to match.",
    ],
  },
  toxic_backlinks: {
    label: "Toxic Backlinks",
    category: "Backlinks",
    quickTip: "Links from spammy or low-quality sites that could be hurting you.",
    definition: "Backlinks flagged with a high toxicity score — usually from spam directories, link farms, or penalized domains — that Google may discount or use to devalue your site.",
    whyItMatters: "A pile of toxic links can suppress rankings site-wide, even if your good links are strong. Cleaning them up removes a hidden anchor holding your rankings down.",
    howToImprove: [
      "Review flagged domains in the Backlinks tab and confirm they're genuinely low quality (not just unfamiliar).",
      "Use the Disavow tool to generate a Google-ready disavow file for the worst offenders.",
      "Avoid buying cheap bulk links going forward — that's usually where toxic links come from.",
    ],
  },
  top3_top10: {
    label: "Top 3 / Top 10 Rankings",
    category: "Rankings & Visibility",
    quickTip: "How many keywords rank in the top 3 vs. the top 10.",
    definition: "Top 3 counts keywords ranking in positions 1-3 (the highest-CTR real estate). Top 10 counts everything on page 1 (positions 1-10).",
    whyItMatters: "Position 1-3 gets the vast majority of clicks. A large Top 10 number with a small Top 3 number means you have page-1 presence but are losing most of the traffic to competitors above you.",
    howToImprove: [
      "Focus content refreshes on your Top 10-but-not-Top-3 keywords — they're the closest wins available.",
      "Add stronger internal links from high-authority pages to these near-miss pages.",
      "Check search intent match — sometimes position is capped because the page doesn't fully answer the query.",
    ],
  },
  keyword_position: {
    label: "Position",
    category: "Keyword Metrics",
    quickTip: "Where this keyword currently ranks in Google search results.",
    definition: "The current ranking position (1 = top result) for this keyword in Google's organic search results.",
    whyItMatters: "Position is the most direct measure of SEO performance for a keyword — it drives everything else: clicks, traffic, and revenue.",
    howToImprove: [
      "Improve on-page relevance: match the exact intent behind the query in your title, H1, and first paragraph.",
      "Build a few quality backlinks pointing directly to the ranking page.",
      "Check Core Web Vitals and page speed — slow pages get outranked by faster competitors with similar content.",
    ],
  },
  keyword_change: {
    label: "Position Change (Δ)",
    category: "Keyword Metrics",
    quickTip: "How much this keyword's position moved since the last check.",
    definition: "The difference between the current position and the previous tracked position. Positive means it moved up (improved); negative means it dropped.",
    whyItMatters: "Sudden drops are early-warning signs of algorithm updates, technical issues, or competitor moves. Sudden gains show you what's working so you can repeat it.",
    howToImprove: [
      "For drops, use the 'Ask Claude why this moved' diagnosis tool to get a likely root cause.",
      "For gains, note what changed recently (new content, new links) and apply the same approach elsewhere.",
      "Track changes over multiple checks before reacting — one-day fluctuations are normal SERP volatility.",
    ],
  },
  search_volume: {
    label: "Search Volume",
    category: "Keyword Metrics",
    quickTip: "Average monthly searches for this keyword.",
    definition: "The average number of times this exact keyword is searched per month, typically averaged over the last 12 months.",
    whyItMatters: "Volume tells you the maximum traffic ceiling for a keyword. Ranking #1 for a 10/mo term won't move the needle the way #1 for a 10,000/mo term will.",
    howToImprove: [
      "This is a market fact, not something you improve — instead, prioritize keywords with volume that matches your traffic goals.",
      "Look for closely related higher-volume variants using the Content Brief tool.",
      "Combine several lower-volume long-tail terms into one comprehensive page to capture cumulative demand.",
    ],
  },
  keyword_difficulty: {
    label: "Keyword Difficulty (KD)",
    category: "Keyword Metrics",
    quickTip: "How hard this keyword is to rank for, 0-100.",
    definition: "A 0-100 score estimating how competitive a keyword is to rank for, based on the authority and content strength of pages currently ranking.",
    whyItMatters: "KD tells you whether a keyword is realistically winnable with your site's current authority — chasing high-KD terms too early wastes months of effort for no result.",
    howToImprove: [
      "For high-KD terms, build supporting content and internal links first to raise your domain's topical authority.",
      "Target lower-KD, closely related keywords for faster wins that compound into authority for harder ones later.",
      "Earn a handful of strong backlinks — link authority is usually the single biggest lever against high-KD terms.",
    ],
  },
  keyword_opportunity_score: {
    label: "Opportunity Score",
    category: "Keyword Metrics",
    quickTip: "0-100: real demand weighed against real difficulty — higher is a better target.",
    definition: "A composite 0-100 score combining a keyword's real DataForSEO search volume (60% weight, capped at 10k/mo) against its real keyword difficulty (40% weight, inverted — easier keywords score higher). It's how keyword research results get ranked best-opportunity-first instead of sorting by raw volume or raw difficulty alone.",
    whyItMatters: "Raw search volume alone tempts you into unwinnable fights against entrenched competitors; raw difficulty alone hides keywords nobody searches for. This score surfaces the sweet spot — real traffic you can realistically win — so you spend credits adding keywords worth tracking.",
    howToImprove: [
      "This is a sorting score, not something to 'improve' directly — use it to prioritize which research results to add first.",
      "A score above 70 usually means solid demand with low competition — add these first.",
      "A low score doesn't mean skip it — check Search Intent too; a low-volume, low-KD transactional term can still convert better than a high-volume informational one.",
    ],
  },
  cpc: {
    label: "CPC (Cost Per Click)",
    category: "Keyword Metrics",
    quickTip: "What advertisers pay per click for this keyword in Google Ads.",
    definition: "The average price advertisers pay per click if they were bidding on this keyword in Google Ads.",
    whyItMatters: "CPC is a strong proxy for commercial value — high-CPC keywords usually mean high buyer intent and real business value if you rank organically instead of paying for ads.",
    howToImprove: [
      "This isn't a metric to 'improve' — use it to prioritize: high CPC + achievable KD is your best ROI target.",
      "If CPC is very low, the keyword may be informational rather than commercial — set expectations accordingly.",
      "Cross-reference with Search Intent to confirm the keyword actually drives buyers, not just browsers.",
    ],
  },
  search_intent: {
    label: "Search Intent",
    category: "Keyword Metrics",
    quickTip: "What the searcher is trying to do: inform, compare, or buy.",
    definition: "Classifies the likely purpose behind a search — Informational (learning), Navigational (finding a specific site), Commercial (comparing options), or Transactional (ready to buy).",
    whyItMatters: "Ranking #1 means nothing if your page doesn't match what the searcher actually wants. Mismatched intent is one of the most common reasons pages plateau below page 1.",
    howToImprove: [
      "Match page format to intent: comparison tables for Commercial, clear pricing/CTA for Transactional, guides for Informational.",
      "Check what's already ranking for the term — Google has already told you the expected intent and format.",
      "Split mismatched pages: don't try to make one page serve both informational and transactional intent.",
    ],
  },
  serp_ai_overview: {
    label: "AI Overview (AIO) Presence",
    category: "Content & AEO",
    quickTip: "Google shows an AI-generated answer box for this search.",
    definition: "Indicates this specific keyword's search results include a Google AI Overview — an AI-generated summary shown above traditional organic results.",
    whyItMatters: "Keywords with an AI Overview see lower click-through to traditional listings unless your content is the one being cited inside the AI answer.",
    howToImprove: [
      "Restructure the target page with a direct, concise answer near the top — that's what AI Overviews pull from.",
      "Add FAQ and HowTo structured data to increase citation odds.",
      "Run the page through the Content / AEO Optimizer for a specific AEO score and fix list.",
    ],
  },
  health_score: {
    label: "Health Score",
    category: "Technical SEO",
    quickTip: "Overall technical SEO score for your site, 0-100.",
    definition: "A composite 0-100 score summarizing your site's technical SEO health, weighted by the severity and count of issues found in the last audit.",
    whyItMatters: "Technical issues (broken pages, slow load times, missing tags) can silently cap your rankings no matter how good your content is. This score is your site's technical vital signs in one number.",
    howToImprove: [
      "Fix all Errors first — they have the biggest score impact and often block indexing entirely.",
      "Work through Warnings next, prioritizing pages with the most organic traffic.",
      "Re-run the audit after each fix batch to confirm the score is moving and catch new issues.",
    ],
  },
  audit_errors: {
    label: "Errors",
    category: "Technical SEO",
    quickTip: "Critical technical issues that likely block indexing or ranking.",
    definition: "The count of critical-severity issues found during the audit — things like broken pages, missing titles, or blocked resources that can directly prevent a page from ranking.",
    whyItMatters: "Errors are the highest-priority fixes — a single error like a noindex tag on a key page can zero out its rankings entirely.",
    howToImprove: [
      "Open the Issues list and fix errors in order of page importance.",
      "Re-crawl (Re-run audit) after fixing to confirm the error count actually drops.",
      "Set a rule: no new page ships without a clean audit pass.",
    ],
  },
  audit_warnings: {
    label: "Warnings",
    category: "Technical SEO",
    quickTip: "Moderate issues worth fixing but not urgent.",
    definition: "Medium-severity issues — like slow-loading pages, thin content, or missing alt text — that hurt performance but don't outright block ranking.",
    whyItMatters: "Individually minor, but warnings compound across a whole site into a meaningful ranking drag, especially for Core Web Vitals-sensitive queries.",
    howToImprove: [
      "Batch-fix warnings by type (e.g. all missing alt text in one pass) rather than page by page.",
      "Prioritize warnings on your highest-traffic pages first.",
      "Recheck Core Web Vitals after image/script fixes — many warnings are performance-related.",
    ],
  },
  audit_notices: {
    label: "Notices",
    category: "Technical SEO",
    quickTip: "Low-priority suggestions and best-practice reminders.",
    definition: "Low-severity, informational flags — best-practice suggestions that rarely affect rankings on their own but are worth cleaning up over time.",
    whyItMatters: "Notices won't make-or-break your rankings, but clearing them keeps your site audit-clean and prevents small issues from becoming bigger ones later.",
    howToImprove: [
      "Address notices during routine content updates rather than as a dedicated sprint.",
      "Use them as a pre-publish checklist for new pages.",
      "Don't let notices distract from fixing Errors and Warnings first.",
    ],
  },
  lcp: {
    label: "Largest Contentful Paint (LCP)",
    category: "Technical SEO",
    quickTip: "How fast your biggest visible element loads. Target ≤ 2.5s.",
    definition: "A Core Web Vital measuring how long it takes for the largest visible element (usually a hero image or heading) to render on screen.",
    whyItMatters: "LCP is a confirmed Google ranking signal and directly reflects the user's 'is this page loading' impatience threshold — slow LCP loses both rankings and visitors.",
    howToImprove: [
      "Compress and lazy-load below-the-fold images; preload the hero image if it's the largest element.",
      "Move render-blocking CSS/JS out of the critical path or defer it.",
      "Upgrade hosting or add a CDN if server response time (TTFB) is the bottleneck.",
    ],
  },
  cls: {
    label: "Cumulative Layout Shift (CLS)",
    category: "Technical SEO",
    quickTip: "How much the page visually jumps around while loading. Target ≤ 0.1.",
    definition: "A Core Web Vital measuring unexpected layout shifts — elements jumping around as the page loads — that frustrate users, e.g. a button moving right as you're about to tap it.",
    whyItMatters: "High CLS creates a jarring experience and is a direct Google ranking factor. It's also one of the easiest Core Web Vitals to fix once identified.",
    howToImprove: [
      "Always set explicit width/height on images and embeds so space is reserved before they load.",
      "Reserve space for ads and dynamically injected content instead of letting it push text down.",
      "Load custom fonts with font-display: optional or preload them to avoid text reflow.",
    ],
  },
  inp: {
    label: "Interaction to Next Paint (INP)",
    category: "Technical SEO",
    quickTip: "How responsive the page feels when clicked/tapped. Target ≤ 200ms.",
    definition: "A Core Web Vital measuring the delay between a user's interaction (click, tap) and the page visually responding. Replaced First Input Delay (FID) as Google's official metric.",
    whyItMatters: "Slow interactivity makes a page feel broken even if it loaded fast — and it's a ranking factor. It especially matters on JS-heavy pages.",
    howToImprove: [
      "Break up long JavaScript tasks so the main thread isn't blocked during interactions.",
      "Reduce third-party scripts (chat widgets, trackers) that hijack the main thread.",
      "Use code-splitting to load only the JS needed for the current view.",
    ],
  },
  mobile_pagespeed: {
    label: "Mobile PageSpeed Score",
    category: "Technical SEO",
    quickTip: "Google PageSpeed score on mobile devices, 0-100. Target ≥ 90.",
    definition: "A 0-100 composite score from Google's PageSpeed Insights simulating a mobile device and typical mobile network conditions.",
    whyItMatters: "Google predominantly crawls and ranks using the mobile version of your site (mobile-first indexing) — a poor mobile score directly caps rankings.",
    howToImprove: [
      "Serve appropriately-sized images for mobile viewports instead of full desktop-size images.",
      "Minimize and defer non-critical JavaScript for the mobile experience.",
      "Test on a mid-range device / throttled connection, not just a fast desktop browser.",
    ],
  },
  desktop_pagespeed: {
    label: "Desktop PageSpeed Score",
    category: "Technical SEO",
    quickTip: "Google PageSpeed score on desktop, 0-100. Target ≥ 90.",
    definition: "A 0-100 composite score from Google's PageSpeed Insights simulating a desktop browser on a typical broadband connection.",
    whyItMatters: "While mobile is primary for ranking, desktop speed still affects conversion rate and user experience for the visitors who do arrive via desktop.",
    howToImprove: [
      "Enable text compression (gzip/brotli) and browser caching for static assets.",
      "Audit and remove unused CSS/JS shipped to every page.",
      "Use a CDN to cut latency for visitors far from your origin server.",
    ],
  },
  total_backlinks: {
    label: "Total Backlinks",
    category: "Backlinks",
    quickTip: "The total number of links pointing to your site.",
    definition: "The total count of individual backlinks (link instances) pointing to any page on your domain, across all referring domains.",
    whyItMatters: "Backlinks remain one of Google's strongest ranking signals — they act as votes of confidence from other sites. Volume matters, but quality (see Domain Rating) matters more.",
    howToImprove: [
      "Publish genuinely link-worthy assets: original data, tools, or in-depth guides others want to cite.",
      "Reach out for guest posts or resource-page mentions on relevant sites in your niche.",
      "Fix and reclaim broken backlinks (links pointing to pages that 404) — that's easy volume you're currently losing.",
    ],
  },
  referring_domains: {
    label: "Referring Domains",
    category: "Backlinks",
    quickTip: "The number of unique websites linking to you.",
    definition: "The count of unique domains linking to your site, regardless of how many individual links each one contains.",
    whyItMatters: "100 links from 100 different domains is far more valuable than 100 links from one domain — diversity of referring domains is a stronger authority signal than raw backlink count.",
    howToImprove: [
      "Prioritize outreach to new domains over getting more links from sites that already link to you.",
      "Diversify link types: guest posts, directories, press mentions, partnerships — not just one channel.",
      "Check the Competitors tab to see which domains link to rivals but not you — those are your next targets.",
    ],
  },
  domain_rating: {
    label: "Domain Rating (DR)",
    category: "Backlinks",
    quickTip: "A 0-100 score estimating a domain's overall backlink authority.",
    definition: "A 0-100 score estimating the strength of a website's backlink profile, based on the quantity and quality of sites linking to it.",
    whyItMatters: "Higher DR domains pass more ranking value through their links. Your own DR is also a rough ceiling on how competitive a keyword you can realistically rank for.",
    howToImprove: [
      "Earn links from genuinely higher-DR sites rather than large volumes of low-DR ones.",
      "Consolidate weak/duplicate pages so link equity isn't spread too thin across your domain.",
      "Disavow toxic links dragging your profile down (see Toxic Backlinks).",
    ],
  },
  dofollow_links: {
    label: "DoFollow Links",
    category: "Backlinks",
    quickTip: "Links that pass SEO authority ('link juice') to your site.",
    definition: "Backlinks without a 'nofollow' attribute — these pass ranking authority ('link equity') from the linking page to yours.",
    whyItMatters: "DoFollow links are the ones that actually move rankings. A healthy backlink profile needs a solid share of DoFollow links, though a natural mix always includes some NoFollow too.",
    howToImprove: [
      "Target editorial mentions and guest posts, which are typically DoFollow by default.",
      "Don't over-optimize — a profile that's 100% DoFollow can look unnatural to Google.",
      "Ask partners/press to link normally rather than aggressively requesting nofollow removal, which can look manipulative.",
    ],
  },
  nofollow_links: {
    label: "NoFollow Links",
    category: "Backlinks",
    quickTip: "Links that don't pass SEO authority, but still drive traffic/brand signals.",
    definition: "Backlinks marked rel=\"nofollow\" (or sponsored/UGC) that tell search engines not to pass ranking authority through them, though they can still drive referral traffic and brand visibility.",
    whyItMatters: "While they don't directly boost rankings, a natural backlink profile always includes NoFollow links (social shares, forums, comments) — their total absence can actually look suspicious.",
    howToImprove: [
      "Don't chase these directly — they typically accumulate naturally from social shares and directories.",
      "Use them as a channel for brand visibility and referral traffic even without ranking value.",
      "Focus link-building energy on earning DoFollow links instead; NoFollow is a natural byproduct of general visibility.",
    ],
  },
  toxic_score: {
    label: "Toxic Score",
    category: "Backlinks",
    quickTip: "0-100 rating of how spammy/risky a specific linking domain is.",
    definition: "A per-domain 0-100 score estimating how spammy or risky a specific referring domain is, based on signals like spam-network membership, irrelevant niches, and low-quality content.",
    whyItMatters: "Links from high-toxic-score domains can do more harm than good. Identifying them is the first step before deciding whether to disavow.",
    howToImprove: [
      "Domains scoring 60+ are candidates for the Disavow tool — export and submit to Google.",
      "Investigate before disavowing: some flagged links are harmless if the domain is just low-authority, not actually spammy.",
      "Prevent future toxic links by avoiding cheap bulk-link services and unmoderated directories.",
    ],
  },
  shared_keywords: {
    label: "Shared Keywords",
    category: "Competitors",
    quickTip: "Keywords both you and a competitor rank for.",
    definition: "The number of keywords where both your domain and a given competitor appear in the ranked results.",
    whyItMatters: "A high overlap means you're going head-to-head on the same search demand — it's your most direct competitive battleground.",
    howToImprove: [
      "Compare positions on shared keywords to see exactly where you're losing to this competitor.",
      "Study their ranking pages for shared terms — what content format or depth is winning?",
      "Use shared-keyword losses to prioritize which pages need a content refresh first.",
    ],
  },
  gap_keywords: {
    label: "Gap Keywords",
    category: "Competitors",
    quickTip: "Keywords a competitor ranks for that you don't rank for at all.",
    definition: "Keywords where a competitor has organic rankings but your domain has none — representing untapped search demand you're not capturing.",
    whyItMatters: "Gap keywords are pure opportunity: your competitor has already proven the demand and format work — you just don't have a page competing yet.",
    howToImprove: [
      "Review the Keyword Gap table and prioritize gaps with high volume and manageable KD.",
      "Create new pages (or expand existing ones) specifically targeting the highest-value gaps.",
      "Use the Content Brief tool to reverse-engineer what's currently ranking for these terms.",
    ],
  },
  aeo_score: {
    label: "AEO Score",
    category: "Content & AEO",
    quickTip: "How well this content is structured to be cited by AI answer engines, 0-100.",
    definition: "A 0-100 score measuring how well a piece of content is structured to be surfaced and cited by AI-powered answer engines (Google AI Overviews, ChatGPT Search, Perplexity).",
    whyItMatters: "As AI answer engines intercept more searches before a click ever happens, being the cited source is quickly becoming as valuable as a #1 ranking used to be.",
    howToImprove: [
      "Answer the target question directly and concisely within the first 1-2 sentences of the section.",
      "Use clear headers, numbered lists, and defined terms — AI systems parse structure more than prose style.",
      "Add supporting data, stats, or unique insight the AI can quote — generic rehashed content rarely gets cited.",
    ],
  },
  word_count: {
    label: "Word Count",
    category: "Content & AEO",
    quickTip: "Total number of words in your draft.",
    definition: "The total word count of the content currently in the editor.",
    whyItMatters: "There's no magic word count for ranking, but content that's too thin often fails to fully answer the query, while padded content dilutes relevance and hurts AEO citability.",
    howToImprove: [
      "Match length to what's already ranking for your target keyword — check competitor depth, not an arbitrary number.",
      "Add depth through genuinely new sub-topics, not repetition of the same point.",
      "Cut filler sentences that exist just to hit a word count — they hurt AEO score, not help it.",
    ],
  },
  keyword_density: {
    label: "Keyword Density",
    category: "Content & AEO",
    quickTip: "What % of your content's words are the target keyword (or close variants).",
    definition: "The percentage of total words in your content that are the exact target keyword or a close variant of it.",
    whyItMatters: "Too low and the page may not seem relevant enough; too high (\"keyword stuffing\") reads unnaturally and can trigger spam signals. Aim for natural, moderate usage.",
    howToImprove: [
      "Use the exact keyword in the title, first paragraph, and one subheading — then let natural variants carry the rest.",
      "If density is very high, replace repeated exact-match instances with synonyms and related phrases.",
      "Write for the reader first; a well-targeted page usually lands in a healthy density range without forcing it.",
    ],
  },
  cannibalization_similarity: {
    label: "Similarity Score",
    category: "Cannibalization",
    quickTip: "How closely two of your pages compete for the same keyword intent.",
    definition: "A percentage score measuring how similar two of your own pages are in targeting the same keyword/intent — high similarity means they're likely competing against each other in search results.",
    whyItMatters: "When two of your pages fight for the same keyword, Google often splits ranking signals between them, so neither ranks as well as one consolidated page would.",
    howToImprove: [
      "Merge the two competing pages into one comprehensive, authoritative page and 301-redirect the weaker one.",
      "If both need to exist, differentiate them clearly — target distinctly different keywords or intents.",
      "Update internal links so they point to the single canonical page you want to rank.",
    ],
  },
  disavow_exports: {
    label: "Disavow Exports",
    category: "Reporting",
    quickTip: "Number of disavow files generated for toxic backlink cleanup.",
    definition: "The count of Google-ready disavow files generated from the Backlinks tool during the reporting window.",
    whyItMatters: "For agencies and clients, this is proof of proactive link-cleanup work — a tangible deliverable showing toxic-link risk was actively managed.",
    howToImprove: [
      "Run a fresh Backlinks refresh regularly so new toxic links are caught and disavowed before they accumulate.",
      "Review the export before submitting to Google Search Console — over-disavowing can remove harmless links.",
      "Track over time: exports should trend down as your link profile gets healthier.",
    ],
  },
  briefs_delivered: {
    label: "Content Briefs Delivered",
    category: "Reporting",
    quickTip: "Number of SERP-grounded content briefs generated this period.",
    definition: "The count of AI-generated, SERP-grounded content briefs produced for this project during the reporting window.",
    whyItMatters: "Briefs are a direct content-production input — this number shows how much content strategy work has actually been handed off to writers.",
    howToImprove: [
      "Generate a brief for every new page before writing starts, rather than after a draft already exists.",
      "Focus briefs on Gap Keywords and Top-10-but-not-Top-3 opportunities for the highest ROI.",
      "Pair each delivered brief with a publishing deadline so briefs turn into live pages, not backlog.",
    ],
  },
  audits_run: {
    label: "Audits Run",
    category: "Reporting",
    quickTip: "Number of technical SEO audits completed this period.",
    definition: "The count of full technical SEO audits (crawls + Core Web Vitals + issue scans) run on this project during the reporting window.",
    whyItMatters: "Regular audits catch regressions (a dev pushes a noindex tag by accident, a plugin breaks page speed) before they quietly tank rankings for weeks.",
    howToImprove: [
      "Re-run an audit after every significant site change — new theme, migration, or major content push.",
      "Aim for at least monthly audits even with no known changes, to catch silent regressions.",
      "Compare Health Score trend across audits, not just the latest number, to spot slow decline early.",
    ],
  },
  keywords_gained: {
    label: "Keywords Gained",
    category: "Reporting",
    quickTip: "Keywords that improved in ranking position this period.",
    definition: "The count of tracked keywords whose position improved (moved up) during the reporting window.",
    whyItMatters: "This is the clearest 'wins' number to show a client or stakeholder — direct evidence that SEO work is translating into better rankings.",
    howToImprove: [
      "Double down on whatever content/link tactics correlate with this period's gainers.",
      "Use the per-keyword rank-drop diagnosis to turn one win into a repeatable playbook.",
      "Report gains alongside Est. Traffic impact so the business value is clear, not just position numbers.",
    ],
  },
  keywords_lost: {
    label: "Keywords Lost",
    category: "Reporting",
    quickTip: "Keywords that dropped in ranking position this period.",
    definition: "The count of tracked keywords whose position declined (moved down) during the reporting window.",
    whyItMatters: "Losses are early warning signals — a rising loss count often precedes a bigger traffic drop and usually points to a specific technical, content, or competitive cause.",
    howToImprove: [
      "Diagnose the biggest losers first using the AI rank-drop explainer to find the root cause.",
      "Check Site Audit for new Errors that appeared around the same time as the drops.",
      "Cross-check if a specific competitor moved up on the same keywords — may need a content refresh to compete.",
    ],
  },
};

export const METRIC_CATEGORIES = [
  "Rankings & Visibility",
  "Traffic",
  "Keyword Metrics",
  "Technical SEO",
  "Backlinks",
  "Competitors",
  "Content & AEO",
  "Cannibalization",
  "Reporting",
];
